import { fetchStartModule } from './ExamsApi'

// Walks forward past any module the student excluded (0 questions) during per-module
// navigation. `initialData` is a `start_custom_exam`/`start_module` response shape
// ({ user_modules: [...], exam: {...}, user_exam_id, ... }). Returns { data } once a
// non-empty module is found, or { ended: true } if none remain (caller should end the exam).
export const skipEmptyModules = async ({ exam_id, user_exam_id, modulesCount }, initialData) => {
    let data = initialData
    while ((data?.user_modules?.[0]?.questions?.length ?? 0) === 0) {
        const nextModuleNumber = data.user_modules[0].module_number + 1
        if (nextModuleNumber >= modulesCount) return { ended: true }
        const response = await fetchStartModule({ exam_id, user_exam_id, module_number: nextModuleNumber })
        if (!response.success) return { error: response.error }
        data = response.data
    }
    return { data }
}

export const normalizeModules = (modules) => {
    let _papers = [];
    let counter = 1;
    let _questions = []
    modules.forEach(module =>{
        module?.questions?.forEach(question => {
            if (question.question_type !== 'block') {
                _questions.push({
                    blocked_question: question.question && question.answers ? false : true,
                    ...question,
                    question_id: `${module.module_number}_${question.question_number}`,
                    virtual_question_number: counter,
                    module_number: module.module_number
                });
                counter += 1;
            }
            if (question.question_type === 'block') {
                const blocks = question.blocks ?? [];
                console.log(question);
                blocks.forEach((block, idx) => {
                    const isFirstBlock = idx === 0;
                    const isLastBlock = idx === blocks?.length - 1;
                    const blockQuestion = { 
                    ...question,
                    ...block,
                    isFirstBlock,
                    isLastBlock,
                    isBlockQuestion: true,
                    blocked_question: block.question && block.answers ? false : true,
                    question_id: `${module.module_number}_${question.question_number}_${block.block_number}`,
                    virtual_question_number: counter,
                    module_number: module.module_number
                    };
                    delete blockQuestion.blocks;
                    _questions.push(blockQuestion);
                    counter += 1;
                });
            }
            if(question.paper !== null && question.paper !== undefined && question.paper !== "") {
                _papers.push({
                    paper: question.paper,
                    question_number: question.question_number,
                    module_number: module.module_number,
                    fontSize: 20,
                    selected_text: []
                })
            }
        });
    });
    return {_questions, _papers};
};

export const formatScore = (value) => {
    if (value === null || value === undefined || value === '') return value
    const num = Number(value)
    if (Number.isNaN(num)) return value
    return Math.round(num * 100) / 100
}

// rows: [{ topic_ids, topic_names, max, value, enabled }]
// Redistributes targetTotal across enabled rows at random: each pass shuffles the
// enabled rows and hands out one question at a time (capped at each row's max) until
// the target is reached, so which topic-groups end up with questions - and how many -
// varies between calls instead of always weighting toward the biggest topic pools.
export const redistributeTopicRows = (rows, targetTotal) => {
    const idxs = rows.map((r, i) => i).filter(i => rows[i].enabled)
    if (idxs.length === 0) return rows
    const sumMax = idxs.reduce((s, i) => s + rows[i].max, 0)
    const clamped = Math.min(Math.max(targetTotal, 0), sumMax)

    const counts = {}
    idxs.forEach(i => { counts[i] = 0 })
    let remaining = clamped
    const order = [...idxs]
    while (remaining > 0) {
        for (let k = order.length - 1; k > 0; k--) {
            const j = Math.floor(Math.random() * (k + 1))
            ;[order[k], order[j]] = [order[j], order[k]]
        }
        let progressed = false
        for (const i of order) {
            if (remaining <= 0) break
            if (counts[i] < rows[i].max) {
                counts[i]++
                remaining--
                progressed = true
            }
        }
        if (!progressed) break // all rows at their max, shouldn't happen since clamped <= sumMax
    }

    return rows.map((r, i) => (idxs.includes(i) ? { ...r, value: counts[i] } : r))
}

export const moduleTotal = (rows) => (rows || []).filter(r => r.enabled).reduce((s, r) => s + r.value, 0)
export const moduleMaxTotal = (rows) => (rows || []).filter(r => r.enabled).reduce((s, r) => s + r.max, 0)

// Sum of moduleTotal across every included, topic-based module - the same overall
// question count shown in the screen's footer, reused to derive the exam-wide duration
export const grandTotalQuestions = (moduleTopicRows, moduleIncluded) =>
    (moduleTopicRows || []).reduce((sum, rows, idx) => sum + (rows && moduleIncluded[idx] !== false ? moduleTotal(rows) : 0), 0)

export const buildTopicsMap = (subjects) => {
    const map = {}
    ;(subjects || []).forEach(subject => {
        ;(subject.topics || []).forEach(topic => {
            map[topic.topic_id] = {
                topic_name: topic.topic_name,
                subject_name: subject.subject_name
            }
        })
    })
    return map
}

export const renderTopicGroupLabel = (row, topicsMap) => {
    if (row.topic_names && row.topic_names.length > 0) {
        return row.topic_names.map((name, idx) => name || topicsMap[row.topic_ids[idx]]?.topic_name || row.topic_ids[idx]).join(', ')
    }
    return row.topic_ids.map(id => topicsMap[id]?.topic_name || id).join(', ')
}

export const isCorrectAnswer = (question) => {
    if(question.question_type === 'multi'){
        let _correct = true
        question?.answers?.forEach((a)=>{
            if( a.is_correct_answer && !question.selected_answer_numbers?.includes(parseInt(a.answer_number)))
                _correct = false
        })
        return _correct
    } else if (question.question_type === 'base' || question.question_type === 'block'){
        return question.answers.find(a=> a.answer_number === question.selected_answer_number && a.is_correct_answer )
    } else if(question.question_type === 'survey'){
        return true
    } else if (question.question_type === 'fill') {
        return question.answers.find(a=> a.text?.toUpperCase() === question.selected_answer_text?.toUpperCase() && a.is_correct_answer)
    }
}